// No imports needed - Sentry is dynamically imported based on environment
/** Optional Sentry: works in both browser (apps provide it) and backend (Node.js). */
let Sentry = null;
let sentryImportPromise = null;
let sentryInitialized = false;
const isBackend = typeof window === 'undefined';
/**
 * Check if we're running in production environment
 */
function isProduction() {
    if (isBackend) {
        // Backend: Check CURR_ENV or NODE_ENV
        return process.env.CURR_ENV === 'production' || process.env.NODE_ENV === 'production';
    }
    else {
        // Frontend: Check Vite env or process.env
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            return (import.meta.env.VITE_CURR_ENV ===
                'production');
        }
        if (typeof process !== 'undefined' && process.env) {
            return process.env.NODE_ENV === 'production';
        }
        return false;
    }
}
/**
 * Check if Sentry is both imported AND initialized
 */
function isSentryAvailable() {
    if (!Sentry)
        return false;
    // Check if Sentry is initialized by checking for getClient
    // getClient returns null if Sentry hasn't been initialized
    try {
        const client = Sentry.getClient?.();
        return client !== null && client !== undefined;
    }
    catch {
        return false;
    }
}
// Initialize Sentry based on environment
if (isBackend) {
    // Backend: Try to access @sentry/google-cloud-serverless
    // In backend, Sentry is already initialized synchronously in instrumentation.js
    // Try synchronous access first (if available), then fall back to dynamic import
    try {
        // Try to access Sentry if it's already been imported/initialized
        // eslint-disable-next-line @typescript-eslint/no-require-imports -- synchronous probe for already-loaded Sentry module
        const backendSentry = require('@sentry/google-cloud-serverless');
        if (backendSentry) {
            Sentry = backendSentry;
            try {
                const client = Sentry?.getClient?.();
                sentryInitialized = client !== null && client !== undefined;
            }
            catch {
                sentryInitialized = false;
            }
        }
    }
    catch {
        // If synchronous require fails, try dynamic import
        void import('@sentry/google-cloud-serverless')
            .then((s) => {
            Sentry = s;
            try {
                const client = Sentry?.getClient?.();
                sentryInitialized = client !== null && client !== undefined;
            }
            catch {
                sentryInitialized = false;
            }
        })
            .catch(() => {
            // Backend Sentry not available - that's okay, will fall back to console
            Sentry = null;
            sentryInitialized = false;
        });
    }
}
else {
    // Frontend: Dynamic import of @sentry/react
    sentryImportPromise = import('@sentry/react')
        .then((s) => {
        Sentry = s;
        // Check if Sentry is already initialized
        try {
            const client = s.getClient?.();
            sentryInitialized = client !== null && client !== undefined;
            if (!sentryInitialized) {
                // eslint-disable-next-line no-console -- logging utility, console is the output mechanism
                console.warn('[createLog] @sentry/react imported but not initialized. Call initializeSentry() before using createLog.');
            }
        }
        catch {
            sentryInitialized = false;
        }
    })
        .catch((error) => {
        // Log import failures for debugging
        // eslint-disable-next-line no-console -- logging utility, console is the output mechanism
        console.warn('[createLog] Failed to import @sentry/react:', error);
        Sentry = null;
        sentryInitialized = false;
    });
}
const LogSeverity = {
    INFO: 'INFO',
    WARNING: 'WARNING',
    ERROR: 'ERROR',
    CRITICAL: 'CRITICAL',
};
/**
 * Convert arbitrary data into Sentry-compatible log attributes.
 * Sentry logs only support string | number | boolean.
 */
function toSentryAttributes(data) {
    if (!data)
        return {};
    const out = {};
    for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            out[key] = value;
            continue;
        }
        if (value == null) {
            out[key] = String(value);
            continue;
        }
        try {
            out[key] = JSON.stringify(value);
        }
        catch {
            out[key] = '[Non-serializable]';
        }
    }
    return out;
}
function getSiteName() {
    if (typeof window === 'undefined')
        return undefined;
    return window.location.hostname.replace(/\.ekhodealer\.com$/i, '');
}
const severityToLoggerMethod = {
    [LogSeverity.INFO]: 'info',
    [LogSeverity.WARNING]: 'warn', // ✅ logger uses "warn" (not "warning")
    [LogSeverity.ERROR]: 'error',
    [LogSeverity.CRITICAL]: 'fatal',
};
const severityToBreadcrumbLevel = {
    [LogSeverity.INFO]: 'info',
    [LogSeverity.WARNING]: 'warning',
    [LogSeverity.ERROR]: 'error',
    [LogSeverity.CRITICAL]: 'fatal',
};
function logToSentry(severity, message, attributes, skipRetry = false) {
    const method = severityToLoggerMethod[severity];
    // Check if Sentry is both imported AND initialized
    const sentryAvailable = isSentryAvailable();
    // If Sentry import is still pending, try to use it once it resolves
    if (!sentryAvailable && sentryImportPromise && !skipRetry) {
        // Don't await - just let it resolve in the background
        // This handles the race condition where createLog is called before import completes
        void sentryImportPromise.then(() => {
            // Re-check after import completes
            if (isSentryAvailable()) {
                // Retry logging now that Sentry is available (skip retry to avoid infinite loop)
                logToSentry(severity, message, attributes, true);
            }
        });
    }
    if (!sentryAvailable) {
        const out = `[${severity}] ${message}`;
        if (method === 'info')
            console.info(out, attributes);
        else if (method === 'warn')
            console.warn(out, attributes);
        else
            console.error(out, attributes);
        return;
    }
    // At this point, Sentry is guaranteed to be non-null and initialized
    const S = Sentry;
    const logger = S.logger;
    if (logger) {
        try {
            // Avoid dynamic indexing to satisfy TS
            switch (method) {
                case 'info':
                    logger.info(message, attributes);
                    return;
                case 'warn':
                    logger.warn(message, attributes);
                    return;
                case 'error':
                    logger.error(message, attributes);
                    return;
                case 'fatal':
                    logger.fatal(message, attributes);
                    return;
            }
        }
        catch {
            // fall through to breadcrumb
        }
    }
    S.addBreadcrumb({
        category: 'log',
        message,
        level: severityToBreadcrumbLevel[severity],
        data: attributes,
    });
}
function setScopeContext(scope, { logName, severity, labels, siteName, }) {
    scope.setTag('logName', logName);
    scope.setTag('severity', severity.toLowerCase());
    if (siteName)
        scope.setTag('siteName', siteName);
    if (labels) {
        for (const [key, value] of Object.entries(labels)) {
            if (typeof value === 'string' ||
                typeof value === 'number' ||
                typeof value === 'boolean') {
                scope.setExtra(key, value);
            }
        }
    }
}
/**
 * Universal logging utility for both frontend and backend.
 *
 * Severity behavior:
 * - INFO / WARNING / ERROR → log only
 * - CRITICAL → log + create Sentry Issue (alertable)
 *
 * Works in:
 * - Frontend (browser): Uses @sentry/react (must be initialized via initializeSentry())
 * - Backend (Node.js): Uses @sentry/google-cloud-serverless (initialized in instrumentation.js)
 *
 * Always call without await.
 */
function createLog(logName, logMessage, severity, labels) {
    // Skip logging if not in production
    if (!isProduction()) {
        return;
    }
    try {
        const siteName = getSiteName();
        const attributes = toSentryAttributes({
            logName,
            siteName,
            ...(labels ?? {}),
        });
        // Always emit a log entry
        logToSentry(severity, logMessage, attributes);
        // Escalate only CRITICAL into an Issue (when Sentry is available)
        if (severity === LogSeverity.CRITICAL) {
            if (isSentryAvailable()) {
                const S = Sentry;
                S.withScope((scope) => {
                    setScopeContext(scope, { logName, severity, labels, siteName });
                    scope.setLevel('fatal');
                    scope.setFingerprint(['createLog', 'critical', logName]);
                    S.captureMessage(logMessage);
                });
            }
            else if (sentryImportPromise) {
                // Wait for Sentry to be available for CRITICAL logs
                void sentryImportPromise.then(() => {
                    if (isSentryAvailable()) {
                        const S = Sentry;
                        S.withScope((scope) => {
                            setScopeContext(scope, { logName, severity, labels, siteName });
                            scope.setLevel('fatal');
                            scope.setFingerprint(['createLog', 'critical', logName]);
                            S.captureMessage(logMessage);
                        });
                    }
                });
            }
        }
    }
    catch (e) {
        // eslint-disable-next-line no-console -- logging utility, console is the output mechanism
        console.error('Error creating log:', e);
    }
}

export { LogSeverity, createLog };
//# sourceMappingURL=createLog.js.map
